import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { uid } from "../lib/uid";
import { Gavel, Plus, MapPin, Package, Truck, Clock, Pencil, Trash2, Trophy, X, Loader2, Play, Bot } from "lucide-react";
import { toast } from "sonner";
import { Attachments } from "../components/Attachments";
import { RemindMeButton } from "../components/RemindMeButton";
import { usePolling } from "../lib/usePolling";
import { usePagination } from "../hooks/usePagination";
import { Pagination } from "../components/Pagination";

// ---- IST-aware date helpers ----
// Treat a naive datetime (no timezone, e.g. "2026-07-15T18:30" from a datetime-local
// input) as IST wall-clock; values with Z/offset (e.g. email received) are respected.
function toISTDate(v) {
  if (!v) return null;
  let s = String(v);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(s)) s += "+05:30";
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

// ---- live countdown ----
function useCountdown(target) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [target]);
  const dt = toISTDate(target);
  if (!dt) return { text: "—", none: true };
  const diff = dt.getTime() - Date.now();
  if (diff <= 0) return { text: "EXPIRED", expired: true };
  const s = Math.floor(diff / 1000);
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const p = (n) => String(n).padStart(2, "0");
  // Friendlier, less-cluttered phrasing so short windows read like "12m 34s".
  let text;
  if (d) text = `${d}d ${p(h)}h ${p(m)}m`;
  else if (h) text = `${h}h ${p(m)}m ${p(sec)}s`;
  else text = `${m}m ${p(sec)}s`;
  return { text, urgent: diff < 15 * 60 * 1000 };
}

function CountdownBadge({ target, testId }) {
  const c = useCountdown(target);
  const cls = c.expired ? "bg-[#8A2526] text-white" : c.urgent ? "bg-[#DA1E28] text-white animate-pulse" : "bg-[#1A1A18] text-white";
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded font-mono text-sm font-semibold tabular-nums ${c.none ? "bg-[#E5E3DB] text-[#68665E]" : cls}`} data-testid={testId}>
      <Clock className="w-3.5 h-3.5" /> {c.text}
    </span>
  );
}

const emptyForm = () => ({ lso_number: "", pickup_location: "", delivery_location: "", material_info: "", vehicles: [{ vehicle_type: "", quantity: 1, _uid: uid() }], quote_deadline: "", per_vehicle_cost: "", transport_days: "", quote_notes: "" });

function fmtDT(v) {
  const d = toISTDate(v);
  if (!d) return v ? String(v) : "";
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" }) + " IST";
}

// Auction is "live" when we're inside its start→end window.
function isAuctionLive(b) {
  if (!b || b.stage !== "auction") return false;
  const end = toISTDate(b.auction_deadline);
  if (!end || end.getTime() <= Date.now()) return false;
  const start = toISTDate(b.auction_start);
  if (start && start.getTime() > Date.now()) return false;
  return true;
}

export default function Bids() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [auctionFor, setAuctionFor] = useState(null);
  const [auctionDeadline, setAuctionDeadline] = useState("");
  const [auctionStart, setAuctionStart] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/bids").then((r) => setBids(r.data)).catch(() => toast.error("Could not load LSOs")).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  usePolling(load, 20000, !showForm && !auctionFor);

  const sorted = [...bids].sort((a, b) => {
    // Live reverse auctions (inside their start→end window) always float to the top.
    const la = isAuctionLive(a), lb = isAuctionLive(b);
    if (la !== lb) return la ? -1 : 1;
    if (la && lb) {
      // Both live: the one closing soonest is most urgent → show it first.
      const ea = toISTDate(a.auction_deadline)?.getTime() ?? Infinity;
      const eb = toISTDate(b.auction_deadline)?.getTime() ?? Infinity;
      if (ea !== eb) return ea - eb;
    }
    return String(b.email_date || b.created_at || "").localeCompare(String(a.email_date || a.created_at || ""));
  });
  const paged = usePagination(sorted, 25);

  async function markBid(b, submitted) {
    try {
      await api.post(`/bids/${b.id}/mark-bid`, { submitted });
      toast.success(submitted ? "Marked as bid ✓" : "Bid mark cleared");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not update"); }
  }

  function openCreate() { setEditingId(null); setForm(emptyForm()); setShowForm(true); }
  function openEdit(b) {
    setEditingId(b.id);
    setForm({ ...emptyForm(), ...b, per_vehicle_cost: b.per_vehicle_cost || "", transport_days: b.transport_days || "",
      vehicles: (b.vehicles?.length ? b.vehicles : [{ vehicle_type: "", quantity: 1 }]).map((v) => ({ ...v, _uid: uid() })) });
    setShowForm(true);
  }
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setVeh = (i, k, v) => setForm((f) => ({ ...f, vehicles: f.vehicles.map((x, idx) => idx === i ? { ...x, [k]: v } : x) }));
  const addVeh = () => setForm((f) => ({ ...f, vehicles: [...f.vehicles, { vehicle_type: "", quantity: 1, _uid: uid() }] }));
  const removeVeh = (i) => setForm((f) => ({ ...f, vehicles: f.vehicles.filter((_, idx) => idx !== i) }));

  async function save() {
    if (!form.lso_number.trim()) { toast.error("LSO number is required"); return; }
    setSaving(true);
    const payload = {
      ...form,
      vehicles: form.vehicles.filter((v) => v.vehicle_type || v.quantity).map((v) => ({ vehicle_type: v.vehicle_type, quantity: Number(v.quantity) || 1 })),
      per_vehicle_cost: Number(form.per_vehicle_cost) || 0,
      transport_days: Number(form.transport_days) || 0,
    };
    delete payload.id; delete payload._id; delete payload.created_at; delete payload.updated_at;
    delete payload.created_by; delete payload.created_by_name; delete payload.stage; delete payload.result; delete payload.result_note; delete payload.auction_start; delete payload.auction_deadline;
    try {
      if (editingId) await api.put(`/bids/${editingId}`, payload);
      else await api.post("/bids", payload);
      toast.success(editingId ? "LSO updated" : "LSO added");
      setShowForm(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  }

  async function remove(b) {
    if (!confirm(`Delete LSO ${b.lso_number}?`)) return;
    try { await api.delete(`/bids/${b.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  }
  async function dismiss(b) {
    try { await api.delete(`/bids/${b.id}`); toast.success("Dismissed"); load(); }
    catch { toast.error("Could not dismiss"); }
  }
  async function startAuction() {
    if (!auctionDeadline) { toast.error("Set the reverse-auction deadline"); return; }
    try { await api.post(`/bids/${auctionFor.id}/auction`, { auction_deadline: auctionDeadline, auction_start: auctionStart || undefined }); toast.success("Reverse auction started"); setAuctionFor(null); setAuctionDeadline(""); setAuctionStart(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not start auction"); }
  }
  async function setResult(b, result) {
    try { await api.patch(`/bids/${b.id}/result`, { result }); toast.success(result === "won" ? "Marked as WON 🎉" : "Marked as Lost"); load(); }
    catch { toast.error("Could not update result"); }
  }

  return (
    <div className="space-y-6" data-testid="bids-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#68665E]">L&amp;T Portal</div>
          <h1 className="font-headings text-3xl font-bold text-[#1A1A18] flex items-center gap-2"><Gavel className="w-7 h-7 text-[#C84B31]" /> LSO Bids</h1>
          <p className="text-sm text-[#68665E] mt-1">Track quote deadlines, reverse auctions and outcomes.</p>
        </div>
        {isAdmin && <button onClick={openCreate} className="btn-primary" data-testid="bid-add-button"><Plus className="w-4 h-4" /> Add LSO</button>}
      </div>

      {loading ? <div className="text-sm text-[#68665E] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        : sorted.length === 0 ? <div className="card-surface p-10 text-center text-[#A09E96]" data-testid="bids-empty">No LSOs yet.{isAdmin ? " Click “Add LSO” to create the first one." : ""}</div>
        : (
        <div>
          <Pagination {...paged} testId="bids-pagination" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {paged.pageItems.map((b) => {
            const totalVeh = (b.vehicles || []).reduce((n, v) => n + (Number(v.quantity) || 0), 0);
            const total = (Number(b.per_vehicle_cost) || 0) * (totalVeh || 0);
            return (
            <div key={b.id} className="card-surface p-5" data-testid={`bid-card-${b.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-headings font-bold text-lg text-[#1A1A18]" data-testid={`bid-lso-${b.id}`}>{b.lso_number}</span>
                    {b.stage === "quoting" && !b.bid_submitted && <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded bg-[#FBE3D6] text-[#C84B31] animate-pulse" data-testid={`bid-action-${b.id}`}>Bid LSO</span>}
                    {b.stage === "quoting" && b.bid_submitted && <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded bg-[#D6EEDD] text-[#2F6F4E]">Bid Submitted</span>}
                    {b.stage === "auction" && <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded bg-[#E7D9F5] text-[#6B21A8]">Reverse Auction</span>}
                    {isAuctionLive(b) && <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded bg-[#DA1E28] text-white inline-flex items-center gap-1 animate-pulse" data-testid={`bid-live-${b.id}`}><span className="w-1.5 h-1.5 rounded-full bg-white" /> Live Now</span>}
                    {b.stage === "auction" && b.bid_submitted && <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded bg-[#D6EEDD] text-[#2F6F4E]">Bid Submitted</span>}
                    {b.stage === "done" && b.result === "won" && <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded bg-[#D6EEDD] text-[#2F6F4E] inline-flex items-center gap-1"><Trophy className="w-3 h-3" /> Won</span>}
                    {b.stage === "done" && b.result === "lost" && <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded bg-[#F5D9D9] text-[#8A2526]">Lost</span>}
                    {b.source === "email" && <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-[#EAF0FB] text-[#2B5FB3] inline-flex items-center gap-1" data-testid={`bid-ai-badge-${b.id}`} title={`Auto-added from email by ${b.created_by_name || "GARV"}`}><Bot className="w-3 h-3" /> Auto-added from email</span>}
                  </div>
                  {(b.email_date || b.created_at) && <div className="text-[11px] text-[#68665E] mt-1" data-testid={`bid-email-date-${b.id}`}>Email received: {fmtDT(b.email_date || b.created_at)}</div>}
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-1 shrink-0">
                    {b.source === "email" && <button onClick={() => dismiss(b)} className="btn-ghost !p-2 text-[#68665E]" title="Dismiss this auto-added LSO" data-testid={`bid-dismiss-${b.id}`}><X className="w-4 h-4" /></button>}
                    <button onClick={() => openEdit(b)} className="btn-ghost !p-2" title="Edit" data-testid={`bid-edit-${b.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(b)} className="btn-ghost !p-2 text-[#8A2526]" title="Delete" data-testid={`bid-delete-${b.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </div>

              <div className="mt-3 space-y-1.5 text-sm text-[#4A4842]">
                <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-[#C84B31] mt-0.5 shrink-0" /><span><span className="font-medium">{b.pickup_location || "—"}</span> → <span className="font-medium">{b.delivery_location || "—"}</span></span></div>
                {b.material_info && <div className="flex items-start gap-2"><Package className="w-4 h-4 text-[#C84B31] mt-0.5 shrink-0" /><span>{b.material_info}</span></div>}
                {(b.vehicles || []).length > 0 && (
                  <div className="flex items-start gap-2"><Truck className="w-4 h-4 text-[#C84B31] mt-0.5 shrink-0" />
                    <span className="flex flex-wrap gap-1.5">{b.vehicles.map((v, i) => <span key={`${v.vehicle_type}-${i}`} className="text-xs bg-[#F5F4F0] border border-[#E5E3DB] rounded px-2 py-0.5">{v.vehicle_type || "Vehicle"} × {v.quantity}</span>)}</span>
                  </div>
                )}
              </div>

              {/* countdown */}
              <div className="mt-4 flex items-center gap-3 flex-wrap">
                {b.stage === "quoting" && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-[#68665E] mb-1">Quote due by</div>
                    <div className="text-sm font-semibold text-[#1A1A18] mb-1.5" data-testid={`bid-quote-deadline-dt-${b.id}`}>{fmtDT(b.quote_deadline) || "—"}</div>
                    <CountdownBadge target={b.quote_deadline} testId={`bid-quote-timer-${b.id}`} />
                  </div>
                )}
                {b.stage === "auction" && (() => {
                  const startDt = toISTDate(b.auction_start);
                  const startFuture = startDt && startDt.getTime() > Date.now();
                  return (
                  <div className="w-full">
                    <div className="flex flex-wrap gap-4 text-xs text-[#4A4842] mb-2" data-testid={`bid-auction-window-${b.id}`}>
                      <span>Auction starts: <strong>{fmtDT(b.auction_start) || "—"}</strong></span>
                      <span>Auction ends: <strong>{fmtDT(b.auction_deadline) || "—"}</strong></span>
                    </div>
                    <div className="text-[10px] uppercase tracking-wide text-[#68665E] mb-1">{startFuture ? "Reverse auction starts in" : "Reverse auction ends in"}</div>
                    <CountdownBadge target={startFuture ? b.auction_start : b.auction_deadline} testId={`bid-auction-timer-${b.id}`} />
                  </div>
                  );
                })()}
                {b.stage === "done" && <div className="text-sm text-[#68665E]">Closed · {b.result === "won" ? "We won this bid 🎉" : "We did not win this bid"}</div>}
              </div>

              {/* our quote (admin fills; everyone sees) */}
              <div className="mt-4 border-t border-[#E5E3DB] pt-3 grid grid-cols-3 gap-2 text-center" data-testid={`bid-quote-${b.id}`}>
                <div><div className="text-xs text-[#68665E]">Per-vehicle cost</div><div className="font-semibold text-[#1A1A18]">{b.per_vehicle_cost ? `₹${Number(b.per_vehicle_cost).toLocaleString("en-IN")}` : "—"}</div></div>
                <div><div className="text-xs text-[#68665E]">Transport days</div><div className="font-semibold text-[#1A1A18]">{b.transport_days || "—"}</div></div>
                <div><div className="text-xs text-[#68665E]">Est. total</div><div className="font-semibold text-[#1A1A18]">{total ? `₹${total.toLocaleString("en-IN")}` : "—"}</div></div>
              </div>
              {b.quote_notes && <div className="mt-2 text-xs text-[#68665E]">Note: {b.quote_notes}</div>}

              {/* actions */}
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                {b.stage !== "done" && !b.bid_submitted && <button onClick={() => markBid(b, true)} className="btn-primary text-sm" data-testid={`bid-mark-bid-${b.id}`}><Gavel className="w-4 h-4" /> We&apos;ve bid</button>}
                {b.stage !== "done" && b.bid_submitted && <button onClick={() => markBid(b, false)} className="btn-ghost text-xs text-[#68665E]" data-testid={`bid-unmark-bid-${b.id}`}>Undo &quot;we&apos;ve bid&quot;</button>}
                {isAdmin && b.stage === "quoting" && <button onClick={() => { setAuctionFor(b); setAuctionDeadline(""); }} className="btn-secondary text-sm" data-testid={`bid-start-auction-${b.id}`}><Play className="w-4 h-4" /> Start reverse auction</button>}
                {b.stage !== "done" && (
                  <>
                    <button onClick={() => setResult(b, "won")} className="btn-secondary text-sm !border-[#2F6F4E] !text-[#2F6F4E]" data-testid={`bid-won-${b.id}`}><Trophy className="w-4 h-4" /> We won</button>
                    <button onClick={() => setResult(b, "lost")} className="btn-secondary text-sm !border-[#8A2526] !text-[#8A2526]" data-testid={`bid-lost-${b.id}`}>We lost</button>
                  </>
                )}
                {b.stage === "done" && isAdmin && <button onClick={() => setResult(b, "")} className="btn-ghost text-xs" data-testid={`bid-reopen-${b.id}`}>Reopen</button>}
                {b.stage !== "done" && <RemindMeButton entityType="bid" entityId={b.id} label={`LSO ${b.lso_number}`} className="btn-ghost text-sm" testId={`bid-remind-${b.id}`} />}
              </div>

              <Attachments context="bid" refId={b.id} title="LSO documents" />
            </div>
          );})}
          </div>
        </div>
      )}

      {/* create / edit modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()} data-testid="bid-form-modal">
            <div className="px-5 py-4 border-b border-[#E5E3DB] flex items-center justify-between">
              <h2 className="font-headings font-bold text-lg text-[#1A1A18]">{editingId ? "Edit LSO" : "Add LSO"}</h2>
              <button onClick={() => setShowForm(false)} className="text-[#68665E]" data-testid="bid-form-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="input-label">LSO Number *</label><input className="input-field" value={form.lso_number} onChange={(e) => setF("lso_number", e.target.value)} data-testid="bid-lso-number" /></div>
                <div><label className="input-label">Time left to quote (deadline)</label><input type="datetime-local" className="input-field" value={form.quote_deadline} onChange={(e) => setF("quote_deadline", e.target.value)} data-testid="bid-quote-deadline" /></div>
                <div><label className="input-label">Pickup location</label><input className="input-field" value={form.pickup_location} onChange={(e) => setF("pickup_location", e.target.value)} data-testid="bid-pickup" /></div>
                <div><label className="input-label">Delivery location</label><input className="input-field" value={form.delivery_location} onChange={(e) => setF("delivery_location", e.target.value)} data-testid="bid-delivery" /></div>
              </div>
              <div><label className="input-label">Material information</label><textarea rows={2} className="input-field" value={form.material_info} onChange={(e) => setF("material_info", e.target.value)} data-testid="bid-material" /></div>

              <div>
                <div className="flex items-center justify-between mb-1"><label className="input-label">Vehicles required</label><button type="button" className="btn-ghost text-xs" onClick={addVeh} data-testid="bid-add-vehicle">+ Add vehicle type</button></div>
                {form.vehicles.map((v, i) => (
                  <div key={v._uid ?? i} className="flex gap-2 mb-2" data-testid={`bid-vehicle-row-${i}`}>
                    <input className="input-field flex-1" placeholder="Type (e.g. 32ft MXL)" value={v.vehicle_type} onChange={(e) => setVeh(i, "vehicle_type", e.target.value)} data-testid={`bid-vehicle-type-${i}`} />
                    <input type="number" min="1" className="input-field w-24" placeholder="Qty" value={v.quantity} onChange={(e) => setVeh(i, "quantity", e.target.value)} data-testid={`bid-vehicle-qty-${i}`} />
                    <button type="button" className="btn-ghost !p-2 text-[#8A2526]" onClick={() => removeVeh(i)} data-testid={`bid-vehicle-remove-${i}`}><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>

              <div className="border-t border-[#E5E3DB] pt-4">
                <div className="text-xs uppercase tracking-wide text-[#68665E] mb-2">Our quote (staff will enter this on the L&amp;T portal)</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><label className="input-label">Per-vehicle cost (₹)</label><input type="number" className="input-field" value={form.per_vehicle_cost} onChange={(e) => setF("per_vehicle_cost", e.target.value)} data-testid="bid-cost" /></div>
                  <div><label className="input-label">Transport days</label><input type="number" className="input-field" value={form.transport_days} onChange={(e) => setF("transport_days", e.target.value)} data-testid="bid-days" /></div>
                </div>
                <div className="mt-3"><label className="input-label">Notes (optional)</label><input className="input-field" value={form.quote_notes} onChange={(e) => setF("quote_notes", e.target.value)} data-testid="bid-notes" /></div>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-[#E5E3DB] flex justify-end gap-2">
              <button onClick={() => setShowForm(false)} className="btn-ghost">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary" data-testid="bid-save-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {editingId ? "Save changes" : "Add LSO"}</button>
            </div>
          </div>
        </div>
      )}

      {/* start-auction modal */}
      {auctionFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setAuctionFor(null)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="auction-modal">
            <div className="px-5 py-4 border-b border-[#E5E3DB] flex items-center justify-between">
              <h2 className="font-headings font-bold text-lg text-[#1A1A18]">Start reverse auction</h2>
              <button onClick={() => setAuctionFor(null)} className="text-[#68665E]"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-[#68665E]">LSO <strong>{auctionFor.lso_number}</strong> — set when the reverse auction closes. A live countdown will show on the card.</p>
              <div><label className="input-label">Reverse auction starts (optional)</label><input type="datetime-local" className="input-field" value={auctionStart} onChange={(e) => setAuctionStart(e.target.value)} data-testid="auction-start" /></div>
              <div><label className="input-label">Reverse auction deadline</label><input type="datetime-local" className="input-field" value={auctionDeadline} onChange={(e) => setAuctionDeadline(e.target.value)} data-testid="auction-deadline" /></div>
            </div>
            <div className="px-5 py-4 border-t border-[#E5E3DB] flex justify-end gap-2">
              <button onClick={() => setAuctionFor(null)} className="btn-ghost">Cancel</button>
              <button onClick={startAuction} className="btn-primary" data-testid="auction-start-button">Start auction</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
